import { toDictionary } from "@tokenterminal/core/utils/toDictionary"
import { type Interval } from "@tokenterminal/tt-analytics-api-types/dist/api/zoom"
import useMetricConfiguration from "hooks/trpc/useMetricConfiguration"
import { toId } from "modules/market-sectors/utils"
import { useMetricBreakdown } from "modules/projects/hooks/useMetricBreakdowns"
import { useProjectsList } from "modules/projects/hooks/useProjectsList"
import { useMemo } from "react"

export function useProjectsLegendData({
  metricSlug,
  interval,
  limit,
}: {
  metricSlug: string
  interval: Interval
  limit: number
}) {
  const metricId = toId(metricSlug)
  const { data: metricConfiguration } = useMetricConfiguration({
    metricId,
  })
  const { data: projects = [] } = useProjectsList()
  const projectsByDataId = useMemo(
    () => toDictionary(projects, "data_id"),
    [projects]
  )

  const {
    data: { data: breakdownData = [] } = {},
    isFetching,
    isLoading,
  } = useMetricBreakdown({
    metricIds: [metricId],
    interval,
    groupBy: "projects",
    enabled: !!metricConfiguration,
  })

  const sortedAndLimitedProjects = useMemo(() => {
    if (isLoading || !metricConfiguration) {
      return []
    }

    const projects =
      breakdownData
        ?.map((dataItem) => {
          const project = projectsByDataId.get(dataItem.data_id)

          let value
          if (metricConfiguration.static) {
            value = dataItem.metrics[metricId]?.latest
          } else {
            value = dataItem.metrics[metricId]?.sum ?? 0
          }

          if (!project || value === undefined || value <= 0) {
            return null
          }

          return {
            data_id: dataItem.data_id,
            value,
            name: project.name,
            href: `/explorer/projects/${project.slug}`,
            logo: project.logo,
          }
        })
        .filter((item): item is NonNullable<typeof item> => item !== null) || []

    return projects
      .sort((a, b) => {
        if (metricConfiguration.is_lower_value_better) {
          return a.value - b.value
        }

        return b.value - a.value
      })
      .slice(0, limit)
  }, [
    isLoading,
    metricConfiguration,
    breakdownData,
    limit,
    projectsByDataId,
    metricId,
  ])

  return {
    isFetching,
    isLoading,
    legendItems: sortedAndLimitedProjects,
  }
}
